
import React, { useState, useMemo } from 'react';
import { Quote, AircraftType, Role, UserAccount, TacticalStatus } from '../types';

interface FRMSPortalProps {
  quotes: Quote[];
  aircrafts: AircraftType[];
  currentRole: Role;
  onUpdateQuote: (quoteId: string, updates: Partial<Quote>) => void;
}

const FRMSPortal: React.FC<FRMSPortalProps> = ({ quotes, aircrafts, currentRole, onUpdateQuote }) => {
  const [dfoSignature, setDfoSignature] = useState('');
  
  const closedQuotes = useMemo(() => {
    if (!Array.isArray(quotes)) return [];
    return quotes.filter(q => q.legs.some(l => l.tacticalStatus === 'CLOSED'));
  }, [quotes]);

  const handleApprove = (quoteId: string) => {
    if (!dfoSignature.trim()) {
      alert("Please provide electronic signature (Full Name) to authorize mission.");
      return;
    }
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return;
    
    const updatedLegs = quote.legs.map(l => ({ 
      ...l, 
      tacticalStatus: 'READY' as TacticalStatus,
      frmsApprovedBy: dfoSignature
    }));
    
    onUpdateQuote(quoteId, { legs: updatedLegs });
    alert("Mission Synthesis Authorized. Schedule moved to READY for dispatch.");
  };

  if (currentRole !== 'DFO' && currentRole !== 'ADMIN') {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-12">
        <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-xl">
           <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
        </div>
        <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4">DFO Clearances Required</h2>
        <p className="text-slate-400 text-sm max-w-md font-medium leading-relaxed uppercase tracking-widest text-[10px]">The Fatigue Risk Management System (FRMS) is restricted to the Director of Flight Operations for mission synthesis and legal sign-off.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-500 pb-24">
      <div className="flex justify-between items-end border-b border-slate-100 pb-8">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tighter mb-2 uppercase">FRMS Authority Portal</h2>
          <div className="flex items-center gap-3">
             <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Directorate of Flight Operations | Safety Synthesis</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">DFO Validation Signature</label>
           <input 
             type="text" 
             placeholder="ENTER FULL NAME TO SIGN" 
             className="bg-white border-2 border-slate-200 rounded-2xl px-6 py-4 text-xs font-black text-slate-800 outline-none focus:border-indigo-600 transition-all uppercase tracking-widest w-64 shadow-sm"
             value={dfoSignature}
             onChange={e => setDfoSignature(e.target.value)}
           />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {closedQuotes.length === 0 ? (
          <div className="p-20 text-center bg-slate-900 rounded-[4rem] border border-white/5 shadow-2xl">
             <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 text-white/20">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
             </div>
             <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">No Missions Awaiting Authority Approval</p>
          </div>
        ) : (
          closedQuotes.map(quote => (
            <div key={quote.id} className="bg-slate-900 rounded-[3.5rem] p-12 text-white shadow-2xl border border-white/5 relative overflow-hidden flex flex-col lg:flex-row gap-12">
               <div className="flex-1 space-y-10 relative z-10">
                  <div className="flex items-center gap-6">
                    <div className="px-6 py-3 bg-indigo-600 rounded-2xl text-xl font-black tracking-wider shadow-lg shadow-indigo-900/50">
                      {quote.aircraftModel || quote.tailNumber || 'TBD'}
                    </div>
                     <div>
                        <h3 className="text-2xl font-black tracking-tight">{quote.customerName}</h3>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{quote.id} • {quote.date}</p>
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                     <ValidationCard 
                       label="Pilot rest & Rest" 
                       state="VALIDATED" 
                       sub="FRMS Log Checked"
                       icon={<svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                     />
                     <ValidationCard 
                       label="Payload Synthesis" 
                       state="APPROVED" 
                       sub="MTOW Compliance"
                       icon={<svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/></svg>}
                     />
                     <ValidationCard 
                       label="Fuel Sufficiency" 
                       state="CONFIRMED" 
                       sub="Reserve + Alternate"
                       icon={<svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/></svg>}
                     />
                  </div>
               </div>

               <div className="w-full lg:w-96 flex flex-col justify-center gap-6 p-10 bg-white/5 rounded-[3rem] border border-white/5 relative z-10">
                  <div className="space-y-2">
                     <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Total Block Requirement</p>
                     <p className="text-4xl font-black font-mono">{(quote.totalBlockTimeMinutes / 60).toFixed(1)} HRS</p>
                  </div>
                  <div className="h-px bg-white/10"></div>
                  <button 
                    onClick={() => handleApprove(quote.id)}
                    className="w-full py-6 bg-indigo-600 text-white rounded-[2rem] font-black text-[10px] uppercase tracking-[0.3em] hover:bg-emerald-500 transition-all shadow-2xl shadow-indigo-900 active:scale-95"
                  >
                    Confirm & Launch Mission
                  </button>
                  <button className="w-full py-4 text-[9px] font-black uppercase text-slate-500 hover:text-rose-400">Escalate for Maintenance Check</button>
               </div>
               
               <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ValidationCard: React.FC<{ label: string; state: string; sub: string; icon: React.ReactNode }> = ({ label, state, sub, icon }) => (
  <div className="bg-white/5 border border-white/10 rounded-3xl p-6 flex gap-4 hover:bg-white/10 transition-all">
     <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center shrink-0">
        {icon}
     </div>
     <div>
        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-xs font-black text-white uppercase tracking-tight">{state}</p>
        <p className="text-[9px] font-bold text-slate-500 mt-0.5">{sub}</p>
     </div>
  </div>
);

export default FRMSPortal;
